/**
 * The world's input contract: a coordinate is a *finite* number.
 *
 * Issue #71 found `WorldGen.biomeAt` returning `undefined` — a `NaN` argument
 * poisoned the domain warp, `Math.floor(NaN)` stayed `NaN`, and `NaN` fails
 * **both** arms of a two-comparison band clamp, so it survived the clamp and
 * indexed `bandOrder` off its domain. That was fixed by sanitising the inputs,
 * which made the lookup total.
 *
 * Issue #76 is the part that fix did not address. **The producer was never
 * found.** A total lookup means a non-finite coordinate now resolves to a
 * perfectly valid biome instead of crashing — quieter, and strictly worse,
 * because the wrong biome is indistinguishable from the right one until someone
 * notices the backdrop is the wrong colour a thousand cells from where it
 * should be.
 *
 * So the sanitiser stays (a total `biomeAt` is a contract several callers rely
 * on) and this sits in front of it as the *witness*. Nothing here changes a
 * value. It only records that the world was asked about a place that does not
 * exist.
 *
 * WHY A STICKY RECORD RATHER THAN A THROW.
 *
 * Throwing would contradict #71 on purpose: `tests/biomeLookup.test.ts` asserts
 * that `biomeAt(NaN, 0)` returns a `BiomeDef`, and that totality is deliberate —
 * the alternative is a crash in the render loop, which is what #71 was. So the
 * default is to record, and `setWorldCoordStrict(true)` is the switch a test or
 * a developer flips when they want the first fault to be fatal and carry a
 * stack.
 *
 * WHY IT NEVER RESETS.
 *
 * `profiler.count` clears every frame, which is right for "draw calls this
 * frame" and wrong for this: a one-frame fault that leaves no trace is exactly
 * the failure mode #76 is about. `worldCoordFaults` accumulates for the life of
 * the page, so the first fault of a session is still on the F3 overlay twenty
 * minutes later.
 *
 * No clock and no console: this compiles into a headless test run unchanged,
 * and the same numbers a developer reads off the overlay are the numbers a test
 * asserts are zero.
 */

export interface WorldCoordFaults {
  /** Non-finite coordinate pairs seen since the page loaded. Never reset. */
  count: number;
  /** The first offending pair, kept because the first one is the cause. */
  firstX: number;
  firstY: number;
  /** Call site label of the first fault, e.g. `'biomeIndexAt'`. */
  firstWhere: string;
  /** The most recent pair, so a live overlay shows current behaviour. */
  lastX: number;
  lastY: number;
  lastWhere: string;
}

export const worldCoordFaults: WorldCoordFaults = {
  count: 0,
  firstX: NaN,
  firstY: NaN,
  firstWhere: '',
  lastX: NaN,
  lastY: NaN,
  lastWhere: '',
};

let strict = false;

/**
 * Make the next fault throw instead of merely being recorded.
 *
 * For tests that want a stack pointing at the producer, and for a developer
 * hunting one interactively. Off by default — see the header.
 */
export function setWorldCoordStrict(on: boolean): void {
  strict = on;
}

/** Forget every fault. For tests that need to start from a known state. */
export function resetWorldCoordFaults(): void {
  worldCoordFaults.count = 0;
  worldCoordFaults.firstX = NaN;
  worldCoordFaults.firstY = NaN;
  worldCoordFaults.firstWhere = '';
  worldCoordFaults.lastX = NaN;
  worldCoordFaults.lastY = NaN;
  worldCoordFaults.lastWhere = '';
}

/**
 * Witness a coordinate on its way into a world lookup.
 *
 * Returns `true` when the pair is a real place. Callers are **not** expected to
 * branch on it — the lookup behind this is total by design — but a caller that
 * genuinely has somewhere else to go may.
 *
 * Two comparisons are what let `NaN` through in the first place (#71), so this
 * asks `Number.isFinite`, which is the only test that is true of exactly the
 * numbers the world is defined over.
 */
export function checkWorldCoord(x: number, y: number, where: string): boolean {
  if (Number.isFinite(x) && Number.isFinite(y)) return true;

  if (worldCoordFaults.count === 0) {
    worldCoordFaults.firstX = x;
    worldCoordFaults.firstY = y;
    worldCoordFaults.firstWhere = where;
  }
  worldCoordFaults.count++;
  worldCoordFaults.lastX = x;
  worldCoordFaults.lastY = y;
  worldCoordFaults.lastWhere = where;

  if (strict) {
    throw new Error(
      `non-finite world coordinate at ${where}: (${x}, ${y}) — ` +
        'something upstream computed a coordinate that is not a place (#76)',
    );
  }
  return false;
}

/** One line for the debug overlay. Empty while nothing has gone wrong. */
export function worldCoordFaultLine(): string {
  const f = worldCoordFaults;
  if (f.count === 0) return '';
  return (
    `!! ${f.count} non-finite world coord(s) — first ${f.firstWhere} ` +
    `(${f.firstX}, ${f.firstY}), last ${f.lastWhere} (${f.lastX}, ${f.lastY})`
  );
}
